import { FloatField } from '@mdvip/shared/components/blocks/FloatField';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatAmountInput, integerDigitsFor } from '@/lib/payments/oneTimePayment.format';
import type { OneTimePaymentFormValues } from '@/lib/payments/oneTimePayment.schema';

/**
 * These two fields wrap `FloatField` with a `Controller` directly rather than reusing
 * `FormFloatField`, because the design keeps a helper line visible when the field is valid (the
 * payment maximum, the earliest date) and the date input needs a `min`. `FormFloatField` renders its
 * hint only for errors and passes no extra input attributes, so it cannot express either. Worth
 * promoting into the shared block if a third form wants the same treatment.
 */

/**
 * Validation messages are dynamic i18n keys from the zod schema, so bypass the strict typed-t. The
 * values argument matters for the amount's range message, which interpolates the bounds the schema
 * itself cannot format (it has no locale). Extra values are ignored by keys that take none.
 */
export function useTranslateError() {
  const { t } = useTranslation('members');
  return t as unknown as (message: string, values?: Record<string, string>) => string;
}

interface AmountFieldProps {
  label: string;
  helper: string;
  /** Formatted bounds for the range error, e.g. `{ min: '$0.01', max: '$50,000.00' }`. */
  rangeValues: Record<string, string>;
  /** The live cap, used to stop the member typing more digits than it could ever allow. */
  maxAmount: number;
}

export function AmountField({ label, helper, rangeValues, maxAmount }: AmountFieldProps) {
  const { control } = useFormContext<OneTimePaymentFormValues>();
  const translateError = useTranslateError();

  return (
    <Controller
      control={control}
      name="amount"
      render={({ field, fieldState }) => (
        <FloatField
          ref={field.ref}
          id="amount"
          label={label}
          prefix="$"
          value={field.value}
          onBlur={field.onBlur}
          onChange={(event) => field.onChange(formatAmountInput(event.target.value, integerDigitsFor(maxAmount)))}
          error={fieldState.invalid}
          hint={fieldState.error?.message ? translateError(fieldState.error.message, rangeValues) : helper}
          inputProps={{ inputMode: 'decimal', className: 'tabular-nums' }}
        />
      )}
    />
  );
}

export function ScheduleDateField({ label, helper, min }: { label: string; helper: string; min: string }) {
  const { control } = useFormContext<OneTimePaymentFormValues>();
  const translateError = useTranslateError();

  return (
    <Controller
      control={control}
      name="scheduleDate"
      render={({ field, fieldState }) => (
        <FloatField
          ref={field.ref}
          id="scheduleDate"
          kind="date"
          label={label}
          value={field.value}
          onBlur={field.onBlur}
          onChange={(event) => field.onChange(event.target.value)}
          error={fieldState.invalid}
          hint={fieldState.error?.message ? translateError(fieldState.error.message) : helper}
          // The browser blocks earlier dates in the picker; the schema still enforces the same
          // bound, since a typed value can bypass `min`.
          inputProps={{ min }}
        />
      )}
    />
  );
}
